import React from 'react';
import { Router, Route, RootRoute, Outlet } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '../react-app/theme/ThemeProvider';
import { DiagramPage } from './pages/DiagramPage';
import { ProcessDetailPage } from './pages/ProcessDetailPage';
import { CreateFolderPage } from './pages/CreateFolderPage';
import { CreateDiagramPage } from './pages/CreateDiagramPage';
import { LoginPage } from './pages/LoginPage';
import TestPersistencePage from './pages/TestPersistencePage';
import EnvTestPage from './pages/EnvTestPage';
import TestCanvasPage from './pages/TestCanvasPage';
import { ProtectedRoute } from '../components/auth/ProtectedRoute';
import { Toaster } from 'sonner';
import { initializeRepositorySync } from '../lib/query';
import { AccessPolicyInvalidationBridge } from '../hooks/useAccessPolicyInvalidations';

const queryClient = new QueryClient();

initializeRepositorySync(queryClient);

const RootComponent: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AccessPolicyInvalidationBridge />
      <ThemeProvider defaultMode="light">
        <div className="h-screen w-screen">
          <React.Suspense fallback={null}>
            <Outlet />
          </React.Suspense>
        </div>
        <Toaster position="top-right" />
      </ThemeProvider>

    </QueryClientProvider>
  );
};

const rootRoute = new RootRoute({
  component: RootComponent,
});

const loginRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>) => ({
    returnTo: (search.returnTo as string) || '/',
  }),
});

const diagramRoute = new Route({
  getParentRoute: () => rootRoute,
  path: 'f/$folderId/d/$diagramId',
  // `dept` narrows a live people-chart projection to one department, and `lens`
  // applies an analytics colouring. Both are lenses over the one live chart, so
  // they live in the URL rather than in generated artifacts — which would need
  // reconciling on rename, and which disclosed (by name and by heatmap field)
  // which sensitive data classes the org holds.
  // Passthrough, not a whitelist: this route previously had no search schema, so
  // anything already riding the URL (share-link tokens, embed flags, deep links)
  // must survive. We only normalise `dept` and `lens`.
  validateSearch: (search: Record<string, unknown>) => {
    const passthrough: Record<string, string> = {};
    for (const [key, value] of Object.entries(search)) {
      if (key !== 'dept' && key !== 'lens' && typeof value === 'string') passthrough[key] = value;
    }
    const dept = typeof search.dept === 'string' && search.dept.trim() ? search.dept : undefined;
    const lens = typeof search.lens === 'string' && search.lens.trim() ? search.lens : undefined;
    return { ...passthrough, ...(dept ? { dept } : {}), ...(lens ? { lens } : {}) };
  },
  component: () => (
    <ProtectedRoute>
      <DiagramPage />
    </ProtectedRoute>
  ),
});

const processDetailRoute = new Route({
  getParentRoute: () => rootRoute,
  path: 'f/$folderId/processes/$processId',
  component: () => (
    <ProtectedRoute>
      <ProcessDetailPage />
    </ProtectedRoute>
  ),
});

const createFolderRoute = new Route({
  getParentRoute: () => rootRoute,
  path: 'f/new',
  component: () => (
    <ProtectedRoute>
      <CreateFolderPage />
    </ProtectedRoute>
  ),
});

const createDiagramInFolderRoute = new Route({
  getParentRoute: () => rootRoute,
  path: 'f/$folderId/d/new',
  component: () => (
    <ProtectedRoute>
      <CreateDiagramPage inFolder={true} />
    </ProtectedRoute>
  ),
});

const createDiagramRoute = new Route({
  getParentRoute: () => rootRoute,
  path: 'd/new',
  component: () => (
    <ProtectedRoute>
      <CreateDiagramPage inFolder={false} />
    </ProtectedRoute>
  ),
});

const testPersistenceRoute = new Route({
  getParentRoute: () => rootRoute,
  path: 'test-persistence',
  component: TestPersistencePage,
});

const envTestRoute = new Route({
  getParentRoute: () => rootRoute,
  path: 'env-test',
  component: EnvTestPage,
});

const testCanvasRoute = new Route({
  getParentRoute: () => rootRoute,
  path: 'test-canvas',
  component: TestCanvasPage,
});

const RootRedirect: React.FC = () => {
  React.useEffect(() => {
    window.location.replace('/dashboard')
  }, [])
  return null
}

const indexRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/',
  component: RootRedirect,
});

const DashboardSubRouteHandler: React.FC = () => {
  React.useEffect(() => {
    const currentPath = window.location.pathname;
    const subPath = currentPath.replace('/dashboard/', '').replace('/dashboard', '');
    if (subPath) {
      window.location.replace(`/dashboard?path=${encodeURIComponent(subPath)}`);
    } else {
      window.location.replace('/dashboard');
    }
  }, []);
  return null;
};

const dashboardSplatRoute = new Route({
  getParentRoute: () => rootRoute,
  path: '/dashboard/$',
  component: DashboardSubRouteHandler,
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  createFolderRoute,
  createDiagramInFolderRoute,
  createDiagramRoute,
  // IMPORTANT: keep "new" routes before the diagramId param route,
  // otherwise `/f/:folderId/d/new` is captured by `/f/:folderId/d/:diagramId`
  // and the app will treat `diagramId = 'new'` (breaking create flows).
  diagramRoute,
  processDetailRoute,
  testPersistenceRoute,
  envTestRoute,
  testCanvasRoute,
  dashboardSplatRoute,
]);
export const router = new Router({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

